// eslint-disable-next-line

import {
  DriedVegetablesValues,
  FishFrequencyValues,
  FruitsValues,
  MeatFrequencyValues,
  StarchyFoodsValues,
  SugarFrequencyValues,
} from 'src/types'

import {
  OnboardingStepType,
  NutritionalPreferences,
  NutritionalWeight,
} from '../../types'
import { wysiwygTransformer } from '../../utils/DomUtils'

// Objectives
export type PrettyOptionsObjectType = {
  [key: string]: { id: string; label: string; weight: NutritionalWeight }
}

const prettyOptionsObject = (
  acc: PrettyOptionsObjectType,
  curr: {
    id:
      | FruitsValues
      | StarchyFoodsValues
      | DriedVegetablesValues
      | FishFrequencyValues
      | MeatFrequencyValues
      | SugarFrequencyValues
    label: string
    weight: NutritionalWeight
  }
): PrettyOptionsObjectType => ({
  ...acc,
  [curr.id]: curr,
})

export const FRUITS_VEGETABLES_QUANTITY_STEP = (
  t?: any,
  tips?: any,
  getOptions?: boolean,
  noMeat?: boolean,
  isForPopin?: boolean
) => {
  const options = [
    ...(noMeat
      ? []
      : [
          {
            id: FruitsValues.PORTIONS_3,
            label: isForPopin
              ? t('popin_fruits_vegetables_quantity_3_portions')
              : t('onboarding_step_fruits_vegetables_quantity_3_portions') ??
                '',
            weight: NutritionalWeight.MINIMAL,
          },
        ]),
    {
      id: FruitsValues.PORTIONS_4,
      label: isForPopin
        ? t('popin_fruits_vegetables_quantity_4_portions')
        : t('onboarding_step_fruits_vegetables_quantity_4_portions') ?? '',
      weight: NutritionalWeight.MEDIUM,
    },
    {
      id: FruitsValues.PORTIONS_5,
      label: isForPopin
        ? t('popin_fruits_vegetables_quantity_5_portions')
        : t('onboarding_step_fruits_vegetables_quantity_5_portions') ?? '',
      weight: NutritionalWeight.OPTIMAL,
    },
  ]

  if (getOptions) {
    return options.reduce(prettyOptionsObject, {})
  }

  return {
    type: OnboardingStepType.SLIDER,
    name: NutritionalPreferences.FRUITS_VEGETABLES_QUANTITY,
    options,
    title: isForPopin
      ? t('popin_fruits_vegetables_quantity_title')
      : t('onboarding_step_fruits_vegetables_quantity_title') ?? '',
    image: {
      src: '/images/onboarding/fruits-vegetables.png',
      alt: '',
      height: 216,
      width: 300,
    },
    texts: (
      <>
        <p>{t('onboarding_step_fruits_vegetables_quantity_text') ?? ''}</p>
      </>
    ),
    tip: {
      icon: 'pin',
      title: t('onboarding_tip_title_reco') ?? '',
      content: wysiwygTransformer(
        tips?.[NutritionalPreferences.FRUITS_VEGETABLES_QUANTITY]
      ),
    },
  }
}

export const STARCHY_FOODS_FREQUENCY_STEP = (
  t?: any,
  tips?: any,
  getOptions?: boolean,
  isForPopin?: boolean
) => {
  const options = [
    {
      id: StarchyFoodsValues.PER_WEEK_1,
      label: isForPopin
        ? t('popin_starchy_foods_frequency_1_per_week')
        : t('onboarding_step_starchy_foods_frequency_1_per_week') ?? '',
      weight: NutritionalWeight.MINIMAL,
    },
    {
      id: StarchyFoodsValues.PER_WEEK_3,
      label: isForPopin
        ? t('popin_starchy_foods_frequency_3_per_week')
        : t('onboarding_step_starchy_foods_frequency_3_per_week') ?? '',
      weight: NutritionalWeight.MEDIUM,
    },
    {
      id: StarchyFoodsValues.PER_WEEK_7,
      label: isForPopin
        ? t('popin_starchy_foods_frequency_7_per_week')
        : t('onboarding_step_starchy_foods_frequency_7_per_week') ?? '',
      weight: NutritionalWeight.OPTIMAL,
    },
  ]
  if (getOptions) {
    return options.reduce(prettyOptionsObject, {})
  }
  return {
    type: OnboardingStepType.SLIDER,
    name: NutritionalPreferences.STARCHY_FOODS_FREQUENCY,
    options,
    image: {
      src: '/images/onboarding/pasta.png',
      alt: '',
      height: 342,
      width: 480,
    },
    title: isForPopin
      ? t('popin_starchy_foods_frequency_title')
      : t('onboarding_step_starchy_foods_frequency_title') ?? '',
    texts: (
      <>
        <p>{t('onboarding_step_starchy_foods_frequency_text') ?? ''}</p>
        <p className="italic">
          {t('onboarding_step_starchy_foods_frequency_text2') ?? ''}
        </p>
      </>
    ),
    tip: {
      icon: 'pin',
      title: t('onboarding_tip_title_reco') ?? '',
      content: wysiwygTransformer(
        tips?.[NutritionalPreferences.STARCHY_FOODS_FREQUENCY]
      ),
    },
  }
}

export const DRIED_VEGETABLES_FREQUENCY_STEP = (
  t?: any,
  tips?: any,
  getOptions?: boolean,
  isForPopin?: boolean
) => {
  const options = [
    {
      id: DriedVegetablesValues.RARELY,
      label: isForPopin
        ? t('popin_dried_vegetables_frequency_rarely')
        : t('onboarding_step_dried_vegetables_frequency_rarely') ?? '',
      weight: NutritionalWeight.MINIMAL,
    },
    {
      id: DriedVegetablesValues.PER_WEEK_1,
      label: isForPopin
        ? t('popin_dried_vegetables_frequency_1_per_week')
        : t('onboarding_step_dried_vegetables_frequency_1_per_week') ?? '',
      weight: NutritionalWeight.MEDIUM,
    },
    {
      id: DriedVegetablesValues.PER_WEEK_2,
      label: isForPopin
        ? t('popin_dried_vegetables_frequency_2_per_week')
        : t('onboarding_step_dried_vegetables_frequency_2_per_week') ?? '',
      weight: NutritionalWeight.OPTIMAL,
    },
  ]

  if (getOptions) {
    return options.reduce(prettyOptionsObject, {})
  }

  return {
    type: OnboardingStepType.SLIDER,
    name: NutritionalPreferences.DRIED_VEGETABLES_FREQUENCY,
    options,
    title: isForPopin
      ? t('popin_dried_vegetables_frequency_title')
      : t('onboarding_step_dried_vegetables_frequency_title') ?? '',
    image: {
      src: '/images/onboarding/beans.png',
      alt: '',
      height: 342,
      width: 480,
    },
    texts: (
      <>
        <p>{t('onboarding_step_dried_vegetables_frequency_text') ?? ''}</p>
        <p className="italic">
          {t('onboarding_step_dried_vegetables_frequency_text2') ?? ''}
        </p>
      </>
    ),
    tip: {
      icon: 'pin',
      title: t('onboarding_tip_title_reco') ?? '',
      content: wysiwygTransformer(
        tips?.[NutritionalPreferences.DRIED_VEGETABLES_FREQUENCY]
      ),
    },
  }
}

export const FISH_FREQUENCY_STEP = (
  t?: any,
  tips?: any,
  getOptions?: boolean,
  isForPopin?: boolean
) => {
  const options = [
    {
      id: FishFrequencyValues.RARELY,
      label: isForPopin
        ? t('popin_fish_frequency_rarely')
        : t('onboarding_fish_frequency_rarely') ?? '',
      weight: NutritionalWeight.MINIMAL,
    },
    {
      id: FishFrequencyValues.PER_WEEK_1,
      label: isForPopin
        ? t('popin_fish_frequency_1_per_week')
        : t('onboarding_fish_frequency_1_per_week') ?? '',
      weight: NutritionalWeight.MEDIUM,
    },
    {
      id: FishFrequencyValues.PER_WEEK_2,
      label: isForPopin
        ? t('popin_fish_frequency_2_per_week')
        : t('onboarding_fish_frequency_2_per_week') ?? '',
      weight: NutritionalWeight.OPTIMAL,
    },
  ]
  if (getOptions) {
    return options.reduce(prettyOptionsObject, {})
  }
  return {
    type: OnboardingStepType.SLIDER,
    name: NutritionalPreferences.FISH_FREQUENCY,
    options,
    image: {
      src: '/images/onboarding/fish.png',
      alt: '',
      height: 342,
      width: 480,
    },
    title: isForPopin
      ? t('popin_fish_frequency_title')
      : t('onboarding_fish_frequency_title') ?? '',
    texts: (
      <>
        <p>{t('onboarding_fish_frequency_text') ?? ''}</p>
      </>
    ),
    tip: {
      icon: 'pin',
      title: t('onboarding_tip_title_reco') ?? '',
      content: wysiwygTransformer(
        tips?.[NutritionalPreferences.FISH_FREQUENCY]
      ),
    },
  }
}

export const MEAT_FREQUENCY_STEP = (
  t?: any,
  tips?: any,
  getOptions?: boolean,
  isForPopin?: boolean
) => {
  const options = [
    {
      id: MeatFrequencyValues.PER_WEEK_5,
      label: isForPopin
        ? t('popin_meat_frequency_5_per_week')
        : t('onboarding_meat_frequency_5_per_week') ?? '',
      weight: NutritionalWeight.MINIMAL,
    },
    {
      id: MeatFrequencyValues.PER_WEEK_3,
      label: isForPopin
        ? t('popin_meat_frequency_3_per_week')
        : t('onboarding_meat_frequency_3_per_week') ?? '',
      weight: NutritionalWeight.MEDIUM,
    },
    {
      id: MeatFrequencyValues.PER_WEEK_2,
      label: isForPopin
        ? t('popin_meat_frequency_2_per_week')
        : t('onboarding_meat_frequency_2_per_week') ?? '',
      weight: NutritionalWeight.OPTIMAL,
    },
  ]
  if (getOptions) {
    return options.reduce(prettyOptionsObject, {})
  }
  return {
    type: OnboardingStepType.SLIDER,
    name: NutritionalPreferences.MEAT_FREQUENCY,
    options,
    image: {
      src: '/images/onboarding/meat.png',
      alt: '',
      height: 342,
      width: 480,
    },
    title: isForPopin
      ? t('popin_meat_frequency_title')
      : t('onboarding_meat_frequency_title') ?? '',
    texts: (
      <>
        <p>{t('onboarding_meat_frequency_text') ?? ''}</p>
      </>
    ),
    tip: {
      icon: 'pin',
      title: t('onboarding_tip_title_reco') ?? '',
      content: wysiwygTransformer(
        tips?.[NutritionalPreferences.MEAT_FREQUENCY]
      ),
    },
  }
}

export const DRINK_FREQUENCY_STEP = (
  t?: any,
  tips?: any,
  getOptions?: boolean
) => {
  const options = [
    {
      id: SugarFrequencyValues.PER_DAY_2,
      label: t('onboarding_drink_frequency_2_per_day') ?? '',
      weight: NutritionalWeight.MINIMAL,
    },
    {
      id: SugarFrequencyValues.PER_DAY_1,
      label: t('onboarding_drink_frequency_1_per_day') ?? '',
      weight: NutritionalWeight.MEDIUM,
    },
    {
      id: SugarFrequencyValues.RARELY,
      label: t('onboarding_drink_frequency_rarely') ?? '',
      weight: NutritionalWeight.OPTIMAL,
    },
    {
      id: SugarFrequencyValues.NEVER,
      label: t('onboarding_drink_frequency_none') ?? '',
      weight: NutritionalWeight.OPTIMAL,
    },
  ]
  if (getOptions) {
    return options.reduce(prettyOptionsObject, {})
  }
  return {
    type: OnboardingStepType.SLIDER,
    name: NutritionalPreferences.DRINK_FREQUENCY,
    options,
    image: {
      src: '/images/onboarding/soda.png',
      alt: '',
      height: 342,
      width: 480,
    },
    title: t('onboarding_drink_frequency_title') ?? '',
    texts: (
      <>
        <p>{t('onboarding_drink_frequency_text') ?? ''}</p>
        <p>{t('onboarding_drink_frequency_text2') ?? ''}</p>
      </>
    ),
    tip: {
      icon: 'pin',
      title: t('onboarding_tip_title_reco') ?? '',
      content: wysiwygTransformer(
        tips?.[NutritionalPreferences.DRINK_FREQUENCY]
      ),
    },
  }
}
